import * as ko from "knockout";
import PromiseRequest from "../../req/PromiseRequest";
import {global, lm} from "../../appContext";
import {IObjectData, IRule} from "../../index";
import {
    getObjectDefinition,
    getObjectType,
    getRawSyncFieldValue,
    getSyncFieldDisplayName,
    getSyncFieldValue,
    IObjectDefinition
} from "../../util/misc";
import {getSearchObjectParams} from "../../util/request";
import {ILinkedObjectParams} from "./index";
import {nsRuleset} from "../ruleset/nsRuleset";
import {nsRoute} from "../../util/route";
import {convertFromServerString} from "../../util/datetime";
import {ObjectTypeId} from "../../constants";
import {getHash, isRoutePermissionOkay} from "../../constants/RouteDefinitions";
import {nsObjectRouteLink} from "./nsObjectRouteLink";
import {moment} from 'moment';
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import linkedHtml from './linked.html?raw';

export let template: string = linkedHtml;

interface IType {
    icon: string
    label: string
}

type TObjectTypeId = ObjectTypeId.User | ObjectTypeId.Group | ObjectTypeId.Department | ObjectTypeId.ApiUser

export class viewModel extends ParsedRouteDataDecorator {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private linkedObjectId!: number;
    private readonly name!: string;
    private readonly obj!: IObjectData;
    private readonly objDef!: IObjectDefinition;
    private readonly objLabel!: string;
    private readonly reqSearch: PromiseRequest = global.getPromiseRequest("searchobject");

    private readonly trans_manualobject: Record<TObjectTypeId, string> = {
        [ObjectTypeId.User]: 'source data manual user',
        [ObjectTypeId.Group]: 'source data manual group',
        [ObjectTypeId.Department]: 'source data manual department',
        [ObjectTypeId.ApiUser]: 'source data manual API user',
    }

    private readonly trans_ageneratedobject: Record<TObjectTypeId, string> = {
        [ObjectTypeId.User]: 'a generated user',
        [ObjectTypeId.Group]: 'a generated group',
        [ObjectTypeId.Department]: 'a generated department',
        [ObjectTypeId.ApiUser]: 'a generated API user',
    }

    private readonly trans_anobject: Record<TObjectTypeId, string> = {
        [ObjectTypeId.User]: 'a user',
        [ObjectTypeId.Group]: 'a group',
        [ObjectTypeId.Department]: 'a department',
        [ObjectTypeId.ApiUser]: 'an API user',
    }

    private readonly trans_object: Record<TObjectTypeId, string> = {
        [ObjectTypeId.User]: 'user',
        [ObjectTypeId.Group]: 'group',
        [ObjectTypeId.Department]: 'department',
        [ObjectTypeId.ApiUser]: 'API user',
    }

    constructor(params: ILinkedObjectParams) {
        super(params);
        const destinationRouteId = nsObjectRouteLink.LinkableRouteIds[this.__routeId as nsObjectRouteLink.TLinkableRouteId]
        const isRoutePermissible = isRoutePermissionOkay(destinationRouteId)
        if (!isRoutePermissible) {
            return
        }
        this.obj = params.object;
        this.objDef = getObjectDefinition(this.obj.type.id)!;
        this.name = getSyncFieldValue(this.objDef.syncfieldDisplayName ?? [], this.obj.syncfields);
        this.objLabel = this.objDef.translation;
        if (this.__routeDef.manual) {
            this.init();
        } else {
            this.onSuccess(this.obj.derivedfrommanualobjectid);
        }
    }

    protected init() {
        const rule: IRule = nsRuleset.generateRule({
            argument: getSyncFieldValue('SourceId', this.obj.syncfields),
            argumentoperator: "Er lik",
            attribute: 'SourceId',
            order: 1
        });

        const ruleset = nsRuleset.generateRuleset({
            companyid: this.obj.companyid,
            objecttype: getObjectType(this.obj.type.id),
            rules: [rule]
        })

        this.reqSearch
            .request(getSearchObjectParams({
                jruleset: JSON.stringify(ruleset),
                objecttypeid: this.obj.type.id,
                onlyids: true,
                resultsprpage: 1,
            }))
            .then((data: number[]) => {
                const linkedId = data[0];
                if (linkedId === undefined) {
                    return;
                }
                this.onSuccess(linkedId);
            });
    }

    private onSuccess(linkedId: number) {
        this.linkedObjectId = linkedId;
        this.isReady(true)
    }

    private getGeneratedObjectDataMessage(): string | null {
        if (!this.isReady()) {
            return null
        }

        const isObjectManual = this.obj.manual;
        const linkedId = this.linkedObjectId;

        if (!isObjectManual && !linkedId) {
            return null;
        }

        let message: string[] = [];

        const isExpiryDatePassed = (() => {
            try {
                const expiryDate = getRawSyncFieldValue('ExpiryDate', this.obj.syncfields)
                if (expiryDate) {
                    return convertFromServerString(expiryDate).isBefore(moment());
                }
            } catch (err) {
                return false
            }
        })()

        const objectTypeId = this.__objectTypeId as TObjectTypeId;
        const t_ago = this.trans_ageneratedobject[objectTypeId]
        const t_ao = this.trans_anobject[objectTypeId]
        const t_mo = this.trans_manualobject[objectTypeId]
        const t_o = this.trans_object[objectTypeId]

        if (isObjectManual) {
            message.push(`this is an object|o:((${t_mo}|ac))`)
            if (linkedId) {
                message.push(`object is generated on basis of these data|o:((` + t_ao + `|c))|name:` + this.getLink())
            } else if (!isExpiryDatePassed) {
                message.push(`an object will be generated in the next synchronization|o:((` + t_ao + `|c))`)
            } else {
                message.push(`there is no generated object|o:((` + t_o + `|ac))`)
            }
        } else {
            // Generated object: link via derivedfrommanualobjectid (set in constructor via onSuccess).
            message.push(`this is an object|o:((` + t_ago + `|ac))`)
            if (linkedId == -1) {
                message.push(`object has been deleted|o:((` + t_mo + `))`)
            } else {
                message.push(`object is generated on basis of these data|o:((` + t_o + `))|name:` + this.getLink())
            }
        }

        return message.map(o => lm.t(o)).join(' ');
    }

    private getGeneratedPositionMessage() {
        let manualPositionIds
        try {
            manualPositionIds = getRawSyncFieldValue('AmanualPosition', this.obj.syncfields)
            if (!manualPositionIds) {
                return null
            }
        } catch (err) {
            return null
        }
        const ids = manualPositionIds.split('|').map(s => parseInt(s)).filter(Boolean);
        if (!ids.length) {
            return null
        }
        let ret = `((One or more positions have been generated for this user based on the following data)): `
        ret += ids.map(id => `<a href="${getHash(this.objDef.routeDetailsManual!, {id: id})}">${id}</a>`).join(', ')
        ret += '.'
        return ret
    }

    private getLink(): string {
        let title: string
        const objectTypeId = this.__objectTypeId as TObjectTypeId;
        if (this.__routeDef.manual) {
            title = this.trans_object[objectTypeId]
        } else {
            title = this.trans_manualobject[objectTypeId]
        }
        title = lm.t(title) ?? ''
        const href = getHash(
            this.__routeDef.manual
                ? this.objDef.routeDetails!
                : this.objDef.routeDetailsManual!,
            {id: this.linkedObjectId},
            true
        ) ?? ''
        const name = this.getName()
        return `<a href="${href}" title="${title}">${name}</a>`
    }

    private getName(): string {
        return getSyncFieldDisplayName(this.obj.type.id, this.obj.syncfields);
    }

    override dispose() {
        super.dispose();
        this.reqSearch.abort();
    }
}